import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  CalendarIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  Squares2X2Icon,
  ListBulletIcon
} from '@heroicons/react/24/outline';
import { Task, PRIORITY_OPTIONS, STATUS_OPTIONS, getPriorityColor, getStatusColor, isOverdue, isDueToday } from '../types/Task';
import { formatDateForDisplay } from '../utils/dateUtils';
import { taskApi } from '../services/taskApi';
import TaskForm from './TaskForm';
import Layout from './Layout';
import { useBranch } from '../contexts/BranchContext';
import PermissionGate from './PermissionGate';
import TaskCompletionStageModal from './TaskCompletionStageModal';
import { StageSelectionData } from '../types/Automation';

const TaskList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentBranch, isViewingAllBranches, loading: branchLoading } = useBranch();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false); // becomes true after the first fetch completes
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(''); // search value the fetch actually uses
  const requestIdRef = useRef(0); // guards against out-of-order responses (latest-wins)
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [originFilter, setOriginFilter] = useState('');
  // Persist scope, view, and page in URL so they survive refresh/navigation
  const scopeParam = searchParams.get('scope');
  const viewParam = searchParams.get('view');
  const pageParam = searchParams.get('page');
  const taskFilter: 'all' | 'my' = (scopeParam === 'my' || scopeParam === 'all') ? scopeParam : 'all';
  const viewMode: 'list' | 'board' = (viewParam === 'board' || viewParam === 'list') ? viewParam : 'list';
  const currentPage = pageParam ? parseInt(pageParam, 10) : 1;
  const [viewMyTasksOnly, setViewMyTasksOnly] = useState(false); // Permission to only view my tasks
  const [viewAllTasks, setViewAllTasks] = useState(true); // Permission to view all tasks
  const [permissionsLoaded, setPermissionsLoaded] = useState(false); // Track if permissions have been loaded
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<string | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<number | null>(null);
  
  // Stage selection modal state
  const [showStageSelectionModal, setShowStageSelectionModal] = useState(false);
  const [stageSelectionData, setStageSelectionData] = useState<StageSelectionData | null>(null);
  const [stageSelectionLoading, setStageSelectionLoading] = useState(false);

  // Fetch permissions on mount
  useEffect(() => {
    console.log('🚀 TaskList component mounted - starting permission fetch');
    
    const fetchPermissions = async () => {
      try {
        const data = await taskApi.getTasksViewPermissions();

        setViewMyTasksOnly(data.viewMyTasksOnly || false);
        setViewAllTasks(data.viewAllTasks !== undefined ? data.viewAllTasks : true);

        // If user can only view their tasks, set scope to 'my' in URL
        if (data.viewMyTasksOnly) {
          setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('scope', 'my');
            return next;
          });
        }
      } catch (err) {
        // On error, fall back to the safe default (view own tasks) rather than
        // leaving the page stuck on the loading spinner forever.
        console.error('Error fetching tasks view permissions:', err);
        setViewAllTasks(true);
      } finally {
        // Always release the gate so the task list can load in every case
        // (success, HTTP error, or network error).
        setPermissionsLoaded(true);
      }
    };

    fetchPermissions();
  }, []);

  // Debounce the search box so we don't fire a request on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearchTerm(searchTerm), 350);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  useEffect(() => {
    // Only fetch tasks if permissions have been loaded to avoid race condition
    if (!branchLoading && permissionsLoaded) {
      fetchTasks();
    }
  }, [currentPage, debouncedSearchTerm, statusFilter, priorityFilter, originFilter, taskFilter, viewMyTasksOnly, permissionsLoaded, currentBranch, isViewingAllBranches, branchLoading, viewMode]);

  const fetchTasks = async () => {
    // Latest-wins: tag this request; a slower earlier request must not overwrite
    // the results of a newer one when it finally resolves.
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      setError(null);

      // Get current user ID for "My Tasks" filter
      const currentUserStr = localStorage.getItem('leadshive_user');
      const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null;
      const currentUserId = currentUser?.id;

      // If user has permission to only view their tasks, always use their ID as filter
      // Otherwise, use the taskFilter state
      const assigneeId = (viewMyTasksOnly || taskFilter === 'my') ? (currentUserId?.toString() || '') : '';

      // Board view: fetch more tasks, no status filter so we get all columns
      const page = viewMode === 'board' ? 1 : currentPage;
      const limit = viewMode === 'board' ? 300 : 20;
      const statusForFetch = viewMode === 'board' ? '' : statusFilter;

      const response = await taskApi.getTasks(
        page,
        limit,
        debouncedSearchTerm,
        statusForFetch,
        priorityFilter,
        '', // categoryId
        assigneeId,
        '', // leadId
        originFilter
      );

      if (requestId !== requestIdRef.current) return; // a newer request superseded this one

      if (response.success) {
        setTasks(response.data);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalRecords(response.pagination?.totalRecords || 0);
      } else {
        setError('Failed to fetch tasks');
      }
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      setError(err.message || 'Failed to fetch tasks');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setHasLoaded(true);
      }
    }
  };

  const resetToFirstPage = () => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('page');
      return next;
    });
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    resetToFirstPage();
  };

  const handleStatusFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    resetToFirstPage();
  };

  const handlePriorityFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPriorityFilter(e.target.value);
    resetToFirstPage();
  };

  const handleOriginFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setOriginFilter(e.target.value);
    resetToFirstPage();
  };

  const handleTaskFilterChange = (filter: 'all' | 'my') => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('scope', filter);
      next.delete('page');
      return next;
    });
  };

  const setViewMode = (mode: 'list' | 'board') => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('view', mode);
      return next;
    });
  };

  const handlePageChange = (page: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (page === 1) {
        next.delete('page');
      } else {
        next.set('page', page.toString());
      }
      return next;
    });
  };

  const handleView = (task: Task) => {
    try {
      sessionStorage.setItem('leadshive_last_page', window.location.pathname + window.location.search);
    } catch (_) {}
    navigate(`/tasks/${task.id}`);
  };

  const handleEdit = async (task: Task) => {
    try {
      setLoading(true);
      // Fetch complete task data with relationships
      const response = await taskApi.getTaskById(task.id);
      if (response.success) {
        setEditingTask(response.data);
        setShowForm(true);
      } else {
        setError('Failed to load task details');
      }
    } catch (error) {
      console.error('Error loading task details:', error);
      setError('Failed to load task details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        setDeletingId(id);
        const response = await taskApi.deleteTask(id);
        if (response.success) {
          const remaining = tasks.filter(task => task.id !== id);
          setTasks(remaining);
          setTotalRecords(prev => Math.max(0, prev - 1));
          // If that was the last row on a page past the first, step back a page
          // (which re-fetches) so the user isn't left staring at an empty page.
          if (remaining.length === 0 && currentPage > 1) {
            handlePageChange(currentPage - 1);
          }
        } else {
          alert('Failed to delete task');
        }
      } catch (error) {
        console.error('Error deleting task:', error);
        alert('Failed to delete task');
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleStatusChange = async (id: number, status: string) => {
    try {
      const response = await taskApi.updateTaskStatus(id, status) as any;
      if (response.success) {
        // Only update status/completedAt. The status response is a bare task with no
        // assignees/supervisors/leads/category, so spreading it would wipe those from
        // the row; keep the existing relations already loaded for this task.
        setTasks(prev => prev.map(task =>
          task.id === id
            ? { ...task, status: status as any, completedAt: response.data?.completedAt ?? task.completedAt }
            : task
        ));

        // Check if stage selection is required (for auto-created tasks with prompt mode)
        if (response.stageSelectionRequired && response.stageSelectionData) {
          setStageSelectionData(response.stageSelectionData);
          setShowStageSelectionModal(true);
        }
      } else {
        // Check if this is a completion condition error
        if (response.completionCondition) {
          const condType = response.completionCondition.type;
          let message = response.message || 'This task has completion requirements.';
          
          if (condType === 'document_upload') {
            const remaining = response.completionCondition.details?.remaining?.length || 0;
            message = `Cannot complete: ${remaining} document(s) still required.`;
          } else if (condType === 'call_completed') {
            message = 'Cannot complete: A call must be made first.';
          } else if (condType === 'form_submitted') {
            message = 'Cannot complete: A form must be submitted first.';
          }
          
          alert(message);
        } else {
          alert(response.message || 'Failed to update task status');
        }
      }
    } catch (error: any) {
      console.error('Error updating task status:', error);
      // Try to extract error message from response
      const errMsg = error?.response?.data?.message || 'Failed to update task status';
      alert(errMsg);
    }
  };

  const handleStageSelection = async (selectedStageId: number) => {
    if (!stageSelectionData) return;
    
    const taskId = stageSelectionData.taskId;
    if (!taskId) {
      console.error('No task ID in selection data');
      return;
    }
    
    setStageSelectionLoading(true);
    try {
      const response = await taskApi.completeWithStageSelection(
        taskId,
        stageSelectionData.selectionId,
        selectedStageId
      );
      
      if (response.success) {
        setShowStageSelectionModal(false);
        setStageSelectionData(null);
        // Refresh tasks to reflect the change
        fetchTasks();
        console.log(`Lead stage updated to: ${response.data?.newStageName}`);
      } else {
        throw new Error(response.message || 'Failed to update lead stage');
      }
    } catch (error: any) {
      console.error('Error selecting stage:', error);
      throw error;
    } finally {
      setStageSelectionLoading(false);
    }
  };

  const handleStageSelectionClose = async () => {
    if (stageSelectionData && stageSelectionData.taskId) {
      // Cancel the pending selection
      try {
        await taskApi.cancelStageSelection(stageSelectionData.taskId, stageSelectionData.selectionId);
      } catch (error) {
        console.error('Error cancelling stage selection:', error);
      }
    }
    setShowStageSelectionModal(false);
    setStageSelectionData(null);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingTask(null);
    fetchTasks();
  };

  const handleFormSave = (task: Task) => {
    if (editingTask) {
      setTasks(prev => prev.map(t => t.id === task.id ? task : t));
    } else {
      setTasks(prev => [task, ...prev]);
    }
    handleFormClose();
  };

  // Kanban drag-and-drop
  const handleBoardDragStart = (task: Task) => setDraggedTask(task);
  const handleBoardDragOver = (e: React.DragEvent, status: string) => {
    e.preventDefault();
    setDragOverStatus(status);
  };
  const handleBoardDragLeave = () => setDragOverStatus(null);
  const handleBoardDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    setDragOverStatus(null);
    if (!draggedTask || draggedTask.status === newStatus) {
      setDraggedTask(null);
      return;
    }
    const taskId = draggedTask.id;
    const originalStatus = draggedTask.status;
    setDraggedTask(null);
    // Optimistic update
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus as Task['status'] } : t));
    try {
      setUpdatingStatusId(taskId);
      const response = await taskApi.updateTaskStatus(taskId, newStatus) as any;
      if (!response.success) {
        // Revert the optimistic move AND tell the user why (e.g. the task has an
        // unmet completion requirement) instead of silently snapping the card back.
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: originalStatus } : t));
        let message = response.message || 'Could not update task status.';
        const condType = response.completionCondition?.type;
        if (condType === 'document_upload') {
          const remaining = response.completionCondition?.details?.remaining?.length || 0;
          message = `Cannot complete: ${remaining} document(s) still required.`;
        } else if (condType === 'call_completed') {
          message = 'Cannot complete: a call must be completed first.';
        } else if (condType === 'form_submitted') {
          message = 'Cannot complete: a form must be submitted first.';
        } else if (condType === 'email_sent') {
          message = 'Cannot complete: an email must be sent first.';
        }
        alert(message);
      } else {
        // Check if stage selection is required
        if (response.stageSelectionRequired && response.stageSelectionData) {
          setStageSelectionData(response.stageSelectionData);
          setShowStageSelectionModal(true);
        }
      }
    } catch (err: any) {
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: originalStatus } : t));
      alert(err?.response?.data?.message || 'Could not update task status.');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const hasActiveFilters = searchTerm || statusFilter || priorityFilter || originFilter;

  const getAssigneeName = (a: any) => {
    const first = a.firstName || a.first_name || '';
    const last = a.lastName || a.last_name || '';
    return `${first} ${last}`.trim() || '?';
  };

  const getLeadName = (lead: any) => {
    if (lead.first_name && lead.last_name) return `${lead.first_name} ${lead.last_name}`.trim();
    return lead.name || lead.email || '?';
  };

  const [failedAvatars, setFailedAvatars] = useState<Set<number>>(new Set());

  const handleAvatarError = (userId: number) => {
    setFailedAvatars(prev => new Set(prev).add(userId));
  };

  const renderUserAvatar = (user: any, size: 'sm' | 'md' = 'sm', colorScheme: 'blue' | 'purple' = 'blue') => {
    const sizeClasses = size === 'sm' ? 'w-6 h-6 text-[10px]' : 'w-7 h-7 text-xs';
    const gradientClasses = colorScheme === 'blue' 
      ? 'from-blue-100 to-blue-200 text-blue-700' 
      : 'from-purple-100 to-purple-200 text-purple-700';
    const name = getAssigneeName(user);
    const initial = (user.firstName || user.first_name || '?').charAt(0).toUpperCase();
    const avatarUrl = user.avatar_url || user.avatarUrl;
    const hasValidAvatar = avatarUrl && !failedAvatars.has(user.id);

    if (hasValidAvatar) {
      const imgSrc = `${avatarUrl}${avatarUrl.includes('?') ? '&' : '?'}v=${user.id}`;
      return (
        <img
          key={user.id}
          src={imgSrc}
          alt={name}
          title={name}
          className={`${sizeClasses} rounded-full border-2 border-white object-cover`}
          onError={() => handleAvatarError(user.id)}
        />
      );
    }

    return (
      <div
        key={user.id}
        className={`${sizeClasses} rounded-full bg-gradient-to-br ${gradientClasses} border-2 border-white flex items-center justify-center font-semibold`}
        title={name}
      >
        {initial}
      </div>
    );
  };

  // Only take over the whole page with a spinner on the very first load. On later
  // refetches (search, filter, page, branch change) keep the current list on screen
  // so the search box never loses focus and the page doesn't blank out.
  if (loading && !hasLoaded) {
    return (
      <Layout title="Tasks" subtitle="Manage and track your tasks">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading tasks...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Tasks" subtitle="Manage and track your tasks">
        <div className="text-center py-12">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <p className="mt-2 text-gray-600">{error}</p>
          <button
            onClick={() => fetchTasks()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Tasks" subtitle="Manage and track your tasks">
      <div className={viewMode === 'board' ? 'flex flex-col h-[calc(100vh-6rem)] overflow-hidden space-y-6' : 'space-y-6'}>

        {/* ========== HEADER ROW ========== */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-medium text-gray-900">
              {totalRecords} {totalRecords === 1 ? 'Task' : 'Tasks'}
              {taskFilter === 'my' && <span className="text-blue-600"> (Mine)</span>}
            </h2>
            {hasActiveFilters && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setPriorityFilter('');
                  setOriginFilter('');
                  resetToFirstPage();
                }}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            {/* View Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <ListBulletIcon className="w-4 h-4 mr-1.5" />
                List
              </button>
              <button
                onClick={() => setViewMode('board')}
                className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'board' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Squares2X2Icon className="w-4 h-4 mr-1.5" />
                Board
              </button>
            </div>
            {/* Scope Toggle */}
            {viewAllTasks && (
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => handleTaskFilterChange('all')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    taskFilter === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  All Tasks
                </button>
                <button
                  onClick={() => handleTaskFilterChange('my')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    taskFilter === 'my' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  My Tasks
                </button>
              </div>
            )}
            <PermissionGate module="tasks" permission="create">
              <button
                onClick={() => setShowForm(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center"
              >
                <PlusIcon className="w-4 h-4 inline mr-2" />
                Create Task
              </button>
            </PermissionGate>
          </div>
        </div>

        {/* Permission Notice */}
        {viewMyTasksOnly && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center space-x-2">
            <UserIcon className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">Showing only your assigned tasks and supervised tasks</span>
          </div>
        )}

        {/* ========== FILTER BAR ========== */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Active Filter Badges */}
          {hasActiveFilters && (
            <div className="px-5 py-3 bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 border-b border-gray-200">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {searchTerm && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-700 text-white">
                      Search: {searchTerm}
                      <button onClick={() => { setSearchTerm(''); resetToFirstPage(); }} className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-white hover:bg-opacity-25">
                        <XCircleIcon className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {statusFilter && viewMode !== 'board' && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-600 text-white">
                      Status: {STATUS_OPTIONS.find(s => s.value === statusFilter)?.label}
                      <button onClick={() => { setStatusFilter(''); resetToFirstPage(); }} className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-white hover:bg-opacity-25">
                        <XCircleIcon className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {priorityFilter && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-600 text-white">
                      Priority: {PRIORITY_OPTIONS.find(p => p.value === priorityFilter)?.label}
                      <button onClick={() => { setPriorityFilter(''); resetToFirstPage(); }} className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-white hover:bg-opacity-25">
                        <XCircleIcon className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {originFilter && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-600 text-white">
                      Origin: {originFilter === 'auto' ? 'Auto' : 'Manual'}
                      <button onClick={() => { setOriginFilter(''); resetToFirstPage(); }} className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-white hover:bg-opacity-25">
                        <XCircleIcon className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSearchTerm(''); setStatusFilter(''); setPriorityFilter(''); setOriginFilter('');
                    resetToFirstPage();
                  }}
                  className="text-xs font-semibold text-gray-600 hover:text-gray-900 px-3 py-1 rounded-md hover:bg-white transition-colors"
                >
                  Clear all
                </button>
              </div>
            </div>
          )}
          {/* Filter Controls */}
          <div className="p-4">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
              <div className="relative lg:col-span-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search tasks by name..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm placeholder-gray-400"
                />
              </div>
              <div className="lg:col-span-2">
                <select
                  value={statusFilter}
                  onChange={handleStatusFilter}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <option value="">All Status</option>
                  {STATUS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="lg:col-span-2">
                <select
                  value={priorityFilter}
                  onChange={handlePriorityFilter}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <option value="">All Priority</option>
                  {PRIORITY_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="lg:col-span-2">
                <select
                  value={originFilter}
                  onChange={handleOriginFilter}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <option value="">All Origin</option>
                  <option value="auto">Auto</option>
                  <option value="manual">Manual</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* ========== BOARD VIEW (Kanban) ========== */}
        {/* The board loads a capped number of tasks; if there are more, say so
            explicitly instead of silently showing incomplete columns. */}
        {viewMode === 'board' && totalRecords > tasks.length && (
          <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
            <span>
              Showing the most recent {tasks.length} of {totalRecords} tasks. Use the search/filters
              or switch to List view to see the rest.
            </span>
          </div>
        )}
        {viewMode === 'board' && (
          <div className="flex-1 min-h-0 flex gap-4 overflow-x-auto overflow-y-hidden pb-2">
            {STATUS_OPTIONS.map((statusOpt) => {
              const columnTasks = tasks.filter((t) => t.status === statusOpt.value);
              const isOver = dragOverStatus === statusOpt.value;
              const dotColor = statusOpt.value === 'pending' ? 'bg-gray-400' :
                               statusOpt.value === 'in_progress' ? 'bg-blue-500' :
                               statusOpt.value === 'completed' ? 'bg-green-500' : 'bg-red-400';
              return (
                <div
                  key={statusOpt.value}
                  className={`flex-shrink-0 w-80 h-full flex flex-col rounded-lg border-2 transition-colors ${
                    isOver ? 'border-blue-400 bg-blue-50/50' : 'border-gray-200 bg-gray-50'
                  }`}
                  onDragOver={(e) => handleBoardDragOver(e, statusOpt.value)}
                  onDragLeave={handleBoardDragLeave}
                  onDrop={(e) => handleBoardDrop(e, statusOpt.value)}
                >
                  {/* Column Header */}
                  <div className="flex-shrink-0 px-3.5 py-2.5 border-b border-gray-200 bg-white rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${dotColor}`}></span>
                        <span className="text-sm font-semibold text-gray-900">{statusOpt.label}</span>
                      </div>
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">
                        {columnTasks.length}
                      </span>
                    </div>
                  </div>
                  {/* Column Cards */}
                  <div className="flex-1 min-h-0 p-2 space-y-2 overflow-y-auto">
                    {columnTasks.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 text-sm">No tasks</div>
                    ) : (
                      columnTasks.map((task) => (
                        <div
                          key={task.id}
                          draggable
                          onDragStart={() => handleBoardDragStart(task)}
                          className={`p-3 rounded-lg bg-white border border-gray-200 shadow-sm cursor-move hover:shadow-md hover:border-gray-300 transition-all ${
                            draggedTask?.id === task.id ? 'opacity-50' : ''
                          } ${updatingStatusId === task.id ? 'animate-pulse' : ''}`}
                          onClick={() => handleView(task)}
                        >
                          {/* Title */}
                          <h4 className="text-sm font-medium text-gray-900 line-clamp-2 mb-1.5">{task.name}</h4>
                          {/* Meta tags */}
                          <div className="flex flex-wrap gap-1 mb-2">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getPriorityColor(task.priority)}`}>
                              {PRIORITY_OPTIONS.find(p => p.value === task.priority)?.label}
                            </span>
                            {task.category_name && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: task.category_color }} />
                                {task.category_name}
                              </span>
                            )}
                            {task.isAutoCreated && (
                              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800" title={task.ruleName || ''}>Auto</span>
                            )}
                            {task.completionConditionType && !task.completionConditionMet && (
                              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                                task.completionConditionType === 'document_upload' ? 'bg-amber-100 text-amber-800' :
                                task.completionConditionType === 'call_completed' ? 'bg-green-100 text-green-800' :
                                task.completionConditionType === 'email_sent' ? 'bg-orange-100 text-orange-800' :
                                'bg-purple-100 text-purple-800'
                              }`}>
                                {task.completionConditionType === 'document_upload' ? 'Doc Required' :
                                 task.completionConditionType === 'call_completed' ? 'Call Required' :
                                 task.completionConditionType === 'email_sent' ? 'Email Required' :
                                 'Form Required'}
                              </span>
                            )}
                            {task.completionConditionType && task.completionConditionMet && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                <CheckCircleIcon className="w-3 h-3" /> Met
                              </span>
                            )}
                          </div>
                          {/* Date information */}
                          {task.status === 'completed' && task.completedAt ? (
                            <div className="flex items-center text-xs mb-2 text-green-600">
                              <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
                              Completed {formatDateForDisplay(task.completedAt)}
                            </div>
                          ) : task.dueDate ? (
                            <div className={`flex items-center text-xs mb-2 ${
                              isOverdue(task.dueDate) && task.status !== 'completed' ? 'text-red-600 font-medium' : 
                              isDueToday(task.dueDate) ? 'text-orange-600' : 
                              'text-gray-500'
                            }`}>
                              <CalendarIcon className="w-3.5 h-3.5 mr-1" />
                              {isOverdue(task.dueDate) && task.status !== 'completed' ? 'Overdue: ' : 'Due '}
                              {formatDateForDisplay(task.dueDate)}
                            </div>
                          ) : null}
                          {/* Assignees & Supervisors & Leads Footer */}
                          <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-1">
                            <div className="flex items-center gap-2">
                              {/* Assignees */}
                              {task.assignees && task.assignees.length > 0 ? (
                                <div className="flex items-center gap-1">
                                  <div className="flex -space-x-1.5">
                                    {task.assignees.slice(0, 3).map((a) => renderUserAvatar(a, 'sm', 'blue'))}
                                  </div>
                                  {task.assignees.length > 3 && (
                                    <span className="text-[10px] text-gray-400 font-medium">+{task.assignees.length - 3}</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[10px] text-gray-400 italic flex items-center gap-1">
                                  <UserIcon className="w-3 h-3" /> Unassigned
                                </span>
                              )}
                              {/* Supervisors */}
                              {task.supervisors && task.supervisors.length > 0 && (
                                <div className="flex items-center gap-1 border-l border-gray-200 pl-2">
                                  <div className="flex -space-x-1.5">
                                    {task.supervisors.slice(0, 2).map((s) => renderUserAvatar(s, 'sm', 'purple'))}
                                  </div>
                                  {task.supervisors.length > 2 && (
                                    <span className="text-[10px] text-gray-400 font-medium">+{task.supervisors.length - 2}</span>
                                  )}
                                </div>
                              )}
                            </div>
                            {task.leads && task.leads.length > 0 && (
                              <span className="text-[10px] text-blue-600 font-medium">{task.leads.length} lead{task.leads.length !== 1 ? 's' : ''}</span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ========== LIST VIEW (Table) ========== */}
        {viewMode === 'list' && (tasks.length === 0 ? (
          <div className="text-center py-12">
            <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {hasActiveFilters ? 'No Tasks Found' : 'No Tasks Yet'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {hasActiveFilters
                ? 'Try adjusting your search terms or filters.'
                : 'Get started by creating your first task.'
              }
            </p>
            {!hasActiveFilters && (
              <div className="mt-6">
                <PermissionGate module="tasks" permission="create">
                  <button
                    onClick={() => setShowForm(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Create Task
                  </button>
                </PermissionGate>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Origin</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tasks.map((task) => (
                    <tr
                      key={task.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleView(task)}
                    >
                      {/* Task Name & Meta */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 truncate max-w-[280px]">{task.name}</span>
                              {task.completionConditionType && !task.completionConditionMet && (
                                <span className={`flex-shrink-0 inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                  task.completionConditionType === 'document_upload' ? 'bg-amber-100 text-amber-800' :
                                  task.completionConditionType === 'call_completed' ? 'bg-green-100 text-green-800' :
                                  task.completionConditionType === 'email_sent' ? 'bg-orange-100 text-orange-800' :
                                  'bg-purple-100 text-purple-800'
                                }`}>
                                  {task.completionConditionType === 'document_upload' ? 'Doc' :
                                   task.completionConditionType === 'call_completed' ? 'Call' :
                                   task.completionConditionType === 'email_sent' ? 'Email' : 'Form'}
                                </span>
                              )}
                              {task.completionConditionType && task.completionConditionMet && (
                                <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              {task.category_name && (
                                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: task.category_color }}></span>
                                  {task.category_name}
                                </span>
                              )}
                              {task.leads && task.leads.length > 0 && (
                                <span className="text-xs text-gray-500">
                                  {task.leads.slice(0, 2).map(l => getLeadName(l)).join(', ')}
                                  {task.leads.length > 2 && ` +${task.leads.length - 2}`}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Priority */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(task.priority)}`}>
                          {PRIORITY_OPTIONS.find(p => p.value === task.priority)?.label}
                        </span>
                      </td>

                      {/* Assignees & Supervisors */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {/* Assignees */}
                          {task.assignees && task.assignees.length > 0 ? (
                            <div className="flex items-center gap-1.5">
                              <div className="flex -space-x-1.5">
                                {task.assignees.slice(0, 3).map((a) => renderUserAvatar(a, 'md', 'blue'))}
                              </div>
                              {task.assignees.length > 3 && (
                                <span className="text-xs text-gray-500 font-medium">+{task.assignees.length - 3}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400 italic flex items-center gap-1">
                              <UserIcon className="w-4 h-4" /> None
                            </span>
                          )}
                          {/* Supervisors */}
                          {task.supervisors && task.supervisors.length > 0 && (
                            <div className="flex items-center gap-1.5 border-l border-gray-200 pl-3">
                              <div className="flex -space-x-1.5">
                                {task.supervisors.slice(0, 2).map((s) => renderUserAvatar(s, 'md', 'purple'))}
                              </div>
                              {task.supervisors.length > 2 && (
                                <span className="text-xs text-gray-500 font-medium">+{task.supervisors.length - 2}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Due Date / Completion Date */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {task.status === 'completed' && task.completedAt ? (
                          <div className="flex items-center text-sm text-green-600 font-medium">
                            <CheckCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                            {formatDateForDisplay(task.completedAt)}
                          </div>
                        ) : task.dueDate ? (
                          <div className={`flex items-center text-sm ${
                            isOverdue(task.dueDate) && task.status !== 'completed' ? 'text-red-600 font-medium' :
                            isDueToday(task.dueDate) ? 'text-orange-600 font-medium' :
                            'text-gray-900'
                          }`}>
                            {isOverdue(task.dueDate) && task.status !== 'completed' && <ExclamationTriangleIcon className="w-4 h-4 mr-1 flex-shrink-0" />}
                            {formatDateForDisplay(task.dueDate)}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 italic">No date</span>
                        )}
                      </td>

                      {/* Origin */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {task.isAutoCreated ? (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                            title={task.ruleName ? `Rule: ${task.ruleName}` : 'Auto-created'}
                          >
                            Auto
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500">Manual</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={task.status}
                          onChange={(e) => handleStatusChange(task.id, e.target.value)}
                          className={`text-xs px-2.5 py-1 rounded-full font-semibold border-0 cursor-pointer ${getStatusColor(task.status)} focus:ring-2 focus:ring-blue-500`}
                        >
                          {STATUS_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => handleView(task)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="View Task"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          <PermissionGate module="tasks" permission="edit">
                            <button
                              onClick={() => handleEdit(task)}
                              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
                              title="Edit Task"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate module="tasks" permission="delete">
                            <button
                              onClick={() => handleDelete(task.id)}
                              disabled={deletingId === task.id}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                              title="Delete Task"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-6 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{((currentPage - 1) * 20) + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(currentPage * 20, totalRecords)}</span> of{' '}
                      <span className="font-medium">{totalRecords}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                      </button>
                      {(() => {
                        const pages: (number | string)[] = [];
                        if (totalPages <= 7) {
                          for (let i = 1; i <= totalPages; i++) pages.push(i);
                        } else {
                          pages.push(1);
                          let start = Math.max(2, currentPage - 1);
                          let end = Math.min(totalPages - 1, currentPage + 1);
                          if (currentPage <= 3) { start = 2; end = Math.min(4, totalPages - 1); }
                          else if (currentPage >= totalPages - 2) { start = Math.max(2, totalPages - 3); end = totalPages - 1; }
                          if (start > 2) pages.push('...');
                          for (let i = start; i <= end; i++) pages.push(i);
                          if (end < totalPages - 1) pages.push('...');
                          pages.push(totalPages);
                        }
                        return pages.map((p, idx) =>
                          typeof p === 'string' ? (
                            <span key={`e-${idx}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>
                          ) : (
                            <button
                              key={p}
                              onClick={() => handlePageChange(p)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === p
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {p}
                            </button>
                          )
                        );
                      })()}
                      <button
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Task Form Modal */}
        {showForm && (
          <TaskForm
            task={editingTask}
            onClose={handleFormClose}
            onSave={handleFormSave}
          />
        )}

        {/* Stage Selection Modal */}
        {stageSelectionData && (
          <TaskCompletionStageModal
            isOpen={showStageSelectionModal}
            onClose={handleStageSelectionClose}
            onSelectStage={handleStageSelection}
            selectionData={stageSelectionData}
            isLoading={stageSelectionLoading}
          />
        )}
      </div>
    </Layout>
  );
};

export default TaskList;
